import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { Modal } from "@/shared/ui/modal"
import { yupResolver } from "@hookform/resolvers/yup"
import { SubmitHandler, useForm } from "react-hook-form"
import { selectBonuses, toNano } from "@/entities/bonus"
import { useMemo } from "react"
import { closeTransferBonusesModal, debitBonuses, selectIsTransferModalOpen, transferBonusesSchema, selectOnTransfer } from "../../model"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import BonusSvg from '@/shared/assets/images/BonusMenuIcon.svg'
import TransferSvg from '@/shared/assets/images/ArrowsLeftRight.svg'
import { FormInput } from "@/shared/ui/form-input"

interface TransferBonusesFormValues {
    bonus_id: string,
    amount: number,
}

const initialState: TransferBonusesFormValues = {
    bonus_id: "",
    amount: 0,
}

const createIdempotencyKey = () => {
    if (crypto.randomUUID) {
        return crypto.randomUUID()
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export const TransferBonusesModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectIsTransferModalOpen)
    const onTransfer = useAppSelector(selectOnTransfer)
    const bonuses = useAppSelector(selectBonuses)

    const bonusSelectOptions = useMemo(() => bonuses.map((bonus) => ({
        value: bonus.id,
        label: bonus.name
    })), [bonuses])

    const handleClose = () => {
        dispatch(closeTransferBonusesModal())
    }

    const onSubmit: SubmitHandler<typeof initialState> = async (data) => {
        await new Promise((res, rej) => {
            dispatch(debitBonuses({
                    bonus_id: data.bonus_id,
                    amount_nano: toNano(data.amount),
                    idempotency_key: createIdempotencyKey(),
            }))
            .unwrap()
            .then(() => {
                notify.success('Успешный перевод на социальный кошелек')
                reset()
                onTransfer?.()
                handleClose()
                res('')
            })
            .catch((error) => {
                notify.error(getErrorMessage(error))
                rej()
            })
        })
    }   

    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors, isSubmitting },
        reset,
        getValues
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(transferBonusesSchema)
    })

    const mainBonus = useMemo(() => {
        return bonuses.find(bonus => bonus.id === getValues('bonus_id'))
    }, [bonuses, watch('bonus_id')])

    const amount = useMemo(() => getValues('amount'), [watch('amount')])

    return (
        <Modal
            isOpen={isOpen}
            title={(
                <div>
                    Вывод средств
                </div>
            )}
            onRequestClose={handleClose}       
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
            <FormInput 
                    label={"Бонус"} 
                    id={"bonus_id"} 
                    options={bonusSelectOptions}
                    control={control}
                    error={errors['bonus_id']}
                    type="select"
                    required={true}
                    placeholder="Выберите бонус"
                    components={{
                        ValueContainer: (props) => {
                            return ( 
                                <> 
                                    {props.hasValue &&
                                        <div className="w-8 h-8 flex justify-center mr-2 items-center rounded-full overflow-hidden">
                                            <img src={bonuses?.find((bonus) => bonus.id === props.getValue()?.[0]?.value)?.image} className="w-full object-contain h-full" />
                                        </div>
                                    }
                                    {props.children}
                                </>  
                            )
                        }
                    }}           
                />
                <FormInput 
                    register={register} 
                    label={"Количество бонусов"} 
                    id={"amount"} 
                    type="number"
                    min={0}
                    error={errors['amount']}
                    required={true}
                    placeholder="Укажите количество бонусов"               
                />
                <div className="mt-[12px] mb-1 flex justify-between items-center">
                    <div className="flex rounded-full bg-color-dark py-[6px] px-3 w-full items-center gap-[6px]">
                        <div className="w-6 h-6 rounded-full overflow-hidden">
                            {mainBonus
                                ? <img src={mainBonus.image} className="object-contain" alt="" />
                                : <BonusSvg className={`transition text-color-primary fill-color-dark`} width={24} height={24} />
                            }
                        </div>
                        <div className="font-semibold text-white text-base">
                            {amount}
                        </div>
                    </div>
                    <TransferSvg width={32} height={32} className="mx-3" />
                    <div className={`flex rounded-full py-[6px] px-3 transition w-full items-center gap-[6px] bg-color-dark`}>
                        <BonusSvg className={`transition text-color-primary fill-color-dark`} width={24} height={24} />
                        <div className="font-semibold text-white text-base">
                            {amount}
                        </div>
                    </div>
                </div>
                <div className="w-full flex gap-3">
                    <button type="button" onClick={handleClose} className="form_cancel_button">
                        Отменить
                    </button>
                    <button className="form_handle_button" type="submit" disabled={isSubmitting}>
                        Вывести
                    </button>
                </div>
            </form>
        </Modal>
    )
}
